<script lang="ts" setup>
import { capitalize, lowerFirst } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import { computed, onMounted, ref, watch } from 'vue'
import VueSlider from 'vue-slider-component'

import { useFilterOptions } from '@/filters/composables/useFilterOptions'
import { FilterData, isGroupOption } from '@/store/modules/filters/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const { getOptionValue, displayedOption } = useFilterOptions()

const props = withDefaults(defineProps<{
  filterData: FilterData
  initialValue?: FilterDataValue | null
  isLegacy: boolean
  pmsiType: PmsiTypeEnum
}>(), {
  initialValue: null,
})

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()

const value = ref<(string | number)[]>([])
const subFiltersValues = ref<Record<string, string>>({})

const visibleSubFilters = computed(() => {
  if (!props.filterData.subFilters) return []

  return props.filterData.subFilters.filter(
    (filter) => !filter.pmsiTypes || filter.pmsiTypes.includes(props.pmsiType)
  )
})

const areAllSubFiltersSet = computed(() => {
  if (visibleSubFilters.value.length === 0) return true
  return visibleSubFilters.value.every((filter) => Boolean(subFiltersValues.value[filter.name]?.trim()))
})

const selectedRange = computed(() => {
  if (props.filterData.range) return props.filterData.range

  if (!selectedSubFilterValue.value) return null

  return props.filterData.multiValueRanges?.find(
    (range) => range.values.includes(selectedSubFilterValue.value)
  )?.range ?? null
})

const rangeList = computed(() => {
  const params = selectedRange.value

  if (!params) return []

  const list: (string | number)[] = []
  let currentValue = params.min

  if (params.less) {
    list.push(`<${params.min}`)
  }

  while (currentValue <= params.max) {
    list.push(currentValue)
    currentValue += params.interval
  }
  if (params.more) {
    list.push(`>${params.max}`)
  }

  return list
})

const rangeFormat = computed(() => selectedRange.value?.unit ? `{value}${selectedRange.value.unit}` : '{value}')

const selectedSubFilterValue = computed(() => {
  const filters = visibleSubFilters.value
  for (let i = filters.length - 1; i >= 0; i--) {
    const currentValue = subFiltersValues.value[filters[i].name]
    if (currentValue) return currentValue
  }
  return ''
})

function initializeRange(): void {
  if (!props.initialValue) {
    if (selectedRange.value) {
      const params = selectedRange.value
      const newMin = params.less ? `<${params.min}` : params.min
      const newMax = params.more ? `>${params.max}` : params.max
      resetRange(newMin, newMax)
    }
    return
  }

  if (props.initialValue.value.includes('_')) {
    const [min, max] = props.initialValue.value.split('_')
    const newMin = min.startsWith('<') || min.startsWith('>') ? min : Number(min)
    const newMax = max.startsWith('>') || max.startsWith('<') ? max : Number(max)
    resetRange(newMin, newMax)
  } else {
    resetRange(props.initialValue.value, props.initialValue.value)
  }

  if (props.initialValue.subFilters) {
    subFiltersValues.value = { ...props.initialValue.subFilters }
  }
}

function setRange(): void {
  const { name, format, subFilters } = props.filterData
  let type = format

  if (subFilters) {
    subFilters.forEach((filter) => {
      type = type.replace(
        `{${filter.name}}`,
        capitalize(subFiltersValues.value[filter.name])
      )
    })
    type = lowerFirst(type)
  }

  if (value.value.length) {
    const rangeValue = `${value.value[0]}_${value.value[1]}`
    emit('add-filter', { name, type, value: rangeValue })
  }
}

function resetRange(newMin: string | number, newMax: string | number): void {
  value.value = [newMin, newMax]
}

function initializeSubFiltersValues(): void {
  visibleSubFilters.value.forEach((filter) => {
    subFiltersValues.value[filter.name] = props.initialValue?.subFilters?.[filter.name] ?? filter.default ?? ''
  })
}

function isValueInRange(value: (string | number)[], range: typeof selectedRange.value): boolean {
  if (!range || value.length !== 2) return false

  const allowedValues = rangeList.value
  return allowedValues.includes(value[0]) && allowedValues.includes(value[1])
}

onMounted(() => {
  initializeSubFiltersValues()
  initializeRange()
})

watch(selectedSubFilterValue, (newVal) => {
  if (!newVal) return

  const range = selectedRange.value
  if (!range) return

  const shouldReset = (props.initialValue && !isValueInRange(value.value, range)) || !props.initialValue

  if (shouldReset) {
    const min = range.less ? `<${range.min}` : range.min
    const max = range.more ? `>${range.max}` : range.max
    resetRange(min, max)
  }
})
</script>

<template>
  <div class="row align-items-center">
    <div
      v-if="visibleSubFilters.length > 0"
      class="col-auto d-flex gap-2"
    >
      <select
        v-for="filter in visibleSubFilters"
        :id="`subFilter-` + filter.name"
        :key="filter.name"
        v-model="subFiltersValues[filter.name]"
        class="form-select"
      >
        <option
          value=""
          disabled
          selected
        >
          ---
        </option>
        <option
          v-for="option in filter.options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
          :class="{ 'fw-bold text-dark': isGroupOption(option) && option.isTitle }"
        >
          {{ (option.isTitle === false ? `&nbsp;&nbsp;&nbsp;&nbsp;` : '') + displayedOption(filter, option) }}
        </option>
      </select>
    </div>
    <template v-if="areAllSubFiltersSet">
      <div
        class="col"
        :class="isLegacy ? [] : ['mt-5']"
      >
        <vue-slider
          :key="`${filterData.name}-${selectedSubFilterValue}`"
          v-model="value"
          :v-data="rangeList"
          :formatter="rangeFormat"
          :tooltip="'always'"
          range
        />
      </div>
      <div class="col-auto">
        <button
          id="range-filter-add"
          class="btn btn-primary"
          type="button"
          @click="setRange"
        >
          Ok
        </button>
      </div>
    </template>
  </div>
</template>
